import { Box, MenuItem, MenuList, withStyles } from '@material-ui/core';
import React, { useMemo } from 'react';
import { useBoolean } from 'react-use-boolean';
import { FilterClearButton } from './FilterClearButton';
import { FilterLayout } from './FilterLayout';
import { useFilterContext } from './FilterContext';

export type SingleItemFilterItems<T> = Array<{ label: string; value: T }>;

export interface SingleItemFilterProps<T> {
  label: string;
  columnId: string;
  items: SingleItemFilterItems<T>;
}

const CustomMenu = withStyles({ root: { paddingTop: 0, minWidth: '140px' } })(MenuList);
const CustomMenuItem = withStyles((theme) => ({
  root: {
    fontFamily: theme.typography.h1.fontFamily,
    padding: '4px 16px',
  },
  selected: {
    backgroundColor: 'transparent!important',
    color: theme.palette.primary.main,
  },
}))(MenuItem);

export const SingleItemFilter = <T,>({
  label,
  items,
  columnId,
}: SingleItemFilterProps<T>): JSX.Element => {
  const [open, actions] = useBoolean();

  const { setFilter, getFilterValue } = useFilterContext();

  const value = getFilterValue(columnId);

  const isActive = useMemo(
    () => items.findIndex((item) => item.value === value) !== -1,
    [items, value],
  );

  const onItemClick = (value: T) => {
    setFilter(columnId, value);
    actions.off();
  };

  const onClearClick = () => {
    setFilter(columnId, undefined);
    actions.off();
  };

  return (
    <FilterLayout
      label={label}
      ariaControls='menu-list-grow'
      isActive={isActive}
      open={open}
      openActions={actions}
    >
      <Box>
        <CustomMenu autoFocusItem={open} id='menu-list-grow'>
          {items.map(({ label, value: itemValue }, index) => (
            <CustomMenuItem
              key={index}
              onClick={() => onItemClick(itemValue)}
              selected={value === itemValue}
            >
              {label}
            </CustomMenuItem>
          ))}
        </CustomMenu>
        <Box textAlign='right'>
          <FilterClearButton isActive={isActive} onClick={onClearClick}>
            Clear
          </FilterClearButton>
        </Box>
      </Box>
    </FilterLayout>
  );
};
